/* eslint-disable @typescript-eslint/member-ordering */
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  ConfirmationDialogComponent,
  NavigationBreadcrumbIndicator,
  NavigationBreadcrumbLevel,
  NavigationBreadcrumbOption,
  SnackbarService,
} from '@shared/ui';
import { ApiAuthErrors, AuthService, FeaturesService } from '@telespot/web-core';
import { ComponentStore } from '@ngrx/component-store';
import { BehaviorSubject, EMPTY, Observable, of, combineLatest, merge, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  filter,
  pairwise,
  distinctUntilChanged,
  withLatestFrom,
  shareReplay,
  startWith,
} from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { getRouterSelectors } from '@ngrx/router-store';
import type { FindingParameters } from '@telespot/domain';
import { IAssetItem, IState, Analyst } from '@analysis/domain';
import {
  NavigationStore,
  RoiChangeStore,
  RoiStatsStore,
  AssetStore,
  MicroLayoutStore,
  AnalysisStateStore,
  FindingStore,
  RoiStore,
  AnalystStore,
} from '../../../stores';
import {
  BreadcrumbNavigationEffects,
  RoiStatsEffects,
  RoiRestoreEffects,
  RoiLabelingEffects,
  RoiRevertEffects,
  MicroSyncEffects,
  MicroReviewEffects,
  MicroContextMenuEffects,
  MicroLabelSelectionEffects,
  MicroFindingEffects,
  MicroRoiDrawingEffects,
} from '../../../effects';
import { ETabContext, ImageFilter, RulerType, ViewerMode } from '../../../types/viewer.types';
import { OverlayData } from '@shared/ui';
import { StatsMode } from '../../stats-settings/stats-settings.component';
import { Store } from '@ngrx/store';
import Parse from 'parse';
import { SampleAiAnalysisProgressStore } from './sample-ai-analysis-progress.store';
import { algorithmAiAnalysisLocked } from './sample-ai-analysis-save.guard';
import { ProtocolScope, ProtocolStore } from '@telespot/protocols/ui';
import { Location, formatDate } from '@angular/common';
import {
  Router,
  ActivatedRoute,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';

@Injectable()
export class MicroscopeImageFacade extends ComponentStore<Record<string, never>> {
  private readonly saveBlockedByAiAnalysisSubject = new Subject<void>();
  public readonly saveBlockedByAiAnalysis$ = this.saveBlockedByAiAnalysisSubject.asObservable();
  public hasUnsyncedChanges = false;

  constructor(
    private readonly globalStore: Store,
    private readonly navigationStore: NavigationStore,
    private readonly assetStore: AssetStore,
    private readonly protocolStore: ProtocolStore,
    private readonly microLayout: MicroLayoutStore,
    private readonly findingStore: FindingStore,
    private readonly roiStore: RoiStore,
    private readonly roiStatsStore: RoiStatsStore,
    private readonly analysisStateStore: AnalysisStateStore,
    private readonly analystStore: AnalystStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly roiStatsEffects: RoiStatsEffects,
    private readonly roiRestoreEffects: RoiRestoreEffects,
    private readonly roiLabelingEffects: RoiLabelingEffects,
    private readonly roiRevertEffects: RoiRevertEffects,
    private readonly microSyncEffects: MicroSyncEffects,
    private readonly microReviewEffects: MicroReviewEffects,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly authService: AuthService,
    private readonly featuresService: FeaturesService,
    private readonly snackbarService: SnackbarService,
    private readonly contextMenuEffects: MicroContextMenuEffects,
    private readonly labelSelectionEffects: MicroLabelSelectionEffects,
    private readonly findingEffects: MicroFindingEffects,
    private readonly roiDrawingEffects: MicroRoiDrawingEffects,
    private readonly sampleAiProgressStore: SampleAiAnalysisProgressStore,
    private readonly breadcrumbNavigationEffects: BreadcrumbNavigationEffects,
    private readonly location: Location,
    private readonly translate: TranslateService,
    @Inject(LOCALE_ID) private readonly locale: string
  ) {
    super({});

    const onAssetChanged$ = this.assetParam$.pipe(
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const onSampleIdChanged$ = this.sampleId$.pipe(
      filter((sampleId) => !!sampleId),
      distinctUntilChanged((prev, curr) => prev === curr),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const onStatsLoaded$ = this.roiStatsStore.loading$.pipe(
      pairwise(),
      filter(([prev, curr]) => !!prev && !curr)
    );

    this.wireReactiveEffects();
    this.wireEffectTriggers();
    this.breadcrumbNavigationEffects.loadBreadcrumbOptions();
    // The wireX methods below trigger queries against `Findings`, `Analysis`
    // and `LatestVersion`. Those classes have CLPs restricted to the
    // `analysts` set (admin / analyst / analystmanager). For `technician`
    // and `technicianmanager` users (who enter the viewer purely for
    // image-quality review of their own uploads) we skip them; otherwise
    // every query lands on a 400 and the loading state never resolves.
    if (this.isAnalysisRole) {
      this.wireSampleStatsOnAnalystChange();
      this.wireRestoreUnsavedChangesAfterStats(onStatsLoaded$);
      this.wireAnalystHistoryLoading();
      this.wireAnalystStateEnsured();
      this.wireAnalystUrlSync();
      if (this.canUseAI) {
        this.wireAiProgressDataRefresh();
      }
    }
    this.wireCounterExcluded();
    this.wireAssetInitialization(onSampleIdChanged$, onAssetChanged$);
    this.wireViewerModeOnHistoryChange();
    this.wireDataFetching(onAssetChanged$, onStatsLoaded$);
    // AI progress polling/status is only wired when the organization is
    // licensed for AI review; otherwise no AI status surfaces in the viewer.
    if (this.canUseAI) {
      this.wireSampleAiProgressLoad();
    }
    this.wireAiAnalysisViewOnly();
  }

  private wireAiAnalysisViewOnly(): void {
    combineLatest([this.aiAnalysisStatus$, this.selectedAnalyst$])
      .pipe(
        filter(([status, selectedAnalyst]) => algorithmAiAnalysisLocked(status, selectedAnalyst)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.microLayout.toggleEditing(false);
        this.microLayout.setViewerMode(ViewerMode.idle);
      });
  }

  /**
   * While an algorithm analyst's execution is polling, the AI keeps marking
   * assets as analyzed and producing detections server-side. `status$` stays
   * `inProgress` so it won't re-emit on its own. We use the store's throttled
   * `dataRefreshTick$` to refresh the analyzed-asset markers and the sample
   * detection stats together, so the user sees coherent live progress.
   */
  private wireAiProgressDataRefresh(): void {
    this.sampleAiProgressStore.dataRefreshTick$
      .pipe(
        concatLatestFrom(() => [this.sampleId$, this.selectedAnalyst$]),
        filter(([, sampleId, analyst]) => !!sampleId && !!analyst),
        takeUntil(this.destroy$)
      )
      .subscribe(([, sampleId, analyst]) => {
        this.fetchReferenceStrip({ sampleId: sampleId as string, analyst: analyst as Analyst });
        this.roiStatsEffects.getSampleStats(of({ sampleId: sampleId as string, analyst: analyst as Analyst }));
      });
  }

  private wireSampleAiProgressLoad(): void {
    combineLatest([this.sampleId$, this.methodTypeId$, this.selectedAnalyst$])
      .pipe(
        map(([sampleId, methodTypeId, selectedAnalyst]) =>
          sampleId && methodTypeId ? { sampleId, methodTypeId, poll: selectedAnalyst?.type === 'algorithm' } : null
        ),
        distinctUntilChanged(
          (a, b) => a?.sampleId === b?.sampleId && a?.methodTypeId === b?.methodTypeId && a?.poll === b?.poll
        ),
        tap((params) => {
          this.sampleAiProgressStore.loadProgress(of(params));
        })
      )
      .subscribe();
  }

  private readonly isAnalysisRole: boolean = this.authService.hasRoles(['admin', 'analyst', 'analystmanager']);
  /** Organization licensed for AI analysis review (gates all AI status surfaces). */
  private readonly canUseAI: boolean = this.featuresService.canUse('ai');
  private readonly isTechnicianOnly: boolean = (() => {
    const roles = this.authService.currentUserRoleNames ?? [];
    if (roles.length === 0) return false;
    return roles.every((role) => role === 'technician' || role === 'technicianmanager');
  })();
  private wireEffectTriggers(): void {
    this.afterSyncEffect(this.syncing$);
  }

  private wireSampleStatsOnAnalystChange(): void {
    const onAnalystChangedForStats$ = this.analystStore.selectedAnalyst$.pipe(
      startWith(undefined),
      pairwise(),
      filter(([prev, curr]) => !!curr && prev !== curr),
      map(([, curr]) => curr),
      concatLatestFrom(() => this.sampleId$),
      filter(([, sampleId]) => !!sampleId),
      map(([analyst, sampleId]) => ({ sampleId, analyst }))
    );

    const onSampleIdChangedForStats$ = this.sampleId$.pipe(
      filter((sampleId): sampleId is string => !!sampleId),
      distinctUntilChanged(),
      concatLatestFrom(() => this.analystStore.selectedAnalyst$),
      filter(([, analyst]) => !!analyst),
      map(([sampleId, analyst]) => ({ sampleId, analyst }))
    );

    this.roiStatsEffects.getSampleStats(
      merge(onAnalystChangedForStats$, onSampleIdChangedForStats$).pipe(
        distinctUntilChanged((a, b) => a.sampleId === b.sampleId && a.analyst.id === b.analyst.id)
      )
    );
  }

  private wireCounterExcluded(): void {
    this.roiStatsEffects.getCounterExclusionConfig(this.protocolStore.methodTypeId$);
  }

  private wireRestoreUnsavedChangesAfterStats(onStatsLoaded$: Observable<[boolean, boolean]>): void {
    this.roiRestoreEffects.restoreUnsavedChanges(
      onStatsLoaded$.pipe(
        concatLatestFrom(() => [this.sampleId$, this.analystStore.selectedAnalyst$]),
        map(([, sampleId, analyst]) => ({ sampleId, analystId: analyst.id })),
        distinctUntilChanged((a, b) => a.sampleId === b.sampleId && a.analystId === b.analystId)
      )
    );
  }

  private wireAssetInitialization(onSampleIdChanged$: Observable<string>, onAssetChanged$: Observable<string>): void {
    onSampleIdChanged$.pipe(takeUntil(this.destroy$)).subscribe((sampleId) => {
      // Show the loading overlay for the whole navigation; cleared when the
      // viewer reports the new image opened (see onImageOpened).
      this.startNavigationLoading();
      // Drop any stale label-hover highlight from the previous sample.
      this.hoveredLabelSubject$.next(null);
      this.microLayout.resetAnalyzedAssets();
      this.microLayout.resetReviewedAssets();
      this.assetStore.clearAssets();
      this.roiStore.clearRois();
      this.loadSampleAssets(sampleId);
      this.fetchNavigationReferences(sampleId);
      this.loadProgress(of(sampleId));
      this.fetchProtocol(sampleId);
    });

    // Surface image-load failures (e.g. DZI `/info` 404) in the overlay, but
    // ONLY when the failed asset is the one currently being viewed. Other
    // broken assets in the sample just get a placeholder in the ref-strip and
    // must not block the viewer (you can still navigate to the good ones).
    this.assetStore.assetInfoError$
      .pipe(
        concatLatestFrom(() => this.selectedAsset$),
        takeUntil(this.destroy$)
      )
      .subscribe(([failedAssetId, selectedAsset]) => {
        if (failedAssetId === selectedAsset?.id) this.onImageLoadError();
      });

    this.routeIds$
      .pipe(
        filter(({ sampleId, caseId }) => !!sampleId && !!caseId),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sampleId, caseId }) => {
        this.setCurrentAnalyst(of({ sampleId, caseId }));
      });

    this.selectAsset(
      onAssetChanged$.pipe(
        withLatestFrom(this.selectedAsset$),
        filter(([paramAsset, currentAsset]) => paramAsset !== currentAsset?.id),
        distinctUntilChanged((prev, curr) => prev[0] === curr[0]),
        map(([paramAsset]) => paramAsset)
      )
    );
  }

  private wireAnalystHistoryLoading(): void {
    // The "deep-link / reload" case (URL has historyId on initial load) is
    // handled by `resolveAnalystForSample` via its `preferredHistoryId`
    // So `onHistoryChanged$` only needs to cover the "user picked another analyst from the dropdown"
    // case, where `navigateToUserAnalysis` sets historyId after the protocol
    // is already loaded.
    const onHistoryChanged$ = this.historyId$.pipe(
      concatLatestFrom(() => this.protocolStore.loading$),
      filter(([historyId, protocolLoading]) => !protocolLoading && !!historyId),
      map(([historyId]) => historyId)
    );
    // "User picked own analysis": historyId went truthy → falsy WITHOUT a sample
    // change. The debounceTime collapses the two near-simultaneous emissions of a
    // sample-navigation tick (routeParams + queryParam update separately), so we
    // only react to the settled state. Without this, sample navigation that also
    // clears `historyId` would fire here and override `resolveAnalystForSample`'s
    // preserved selection.
    const onHistoryIdRemoved$ = combineLatest([this.historyId$, this.sampleId$]).pipe(
      debounceTime(0),
      pairwise(),
      filter(
        ([[prevHistoryId, prevSampleId], [currHistoryId, currSampleId]]) =>
          !!prevHistoryId && !currHistoryId && !!currSampleId && prevSampleId === currSampleId
      ),
      map(([, [, sampleId]]) => sampleId)
    );
    this.analystStore.getHistory(onHistoryChanged$);
    this.analystStore.getCurrentAnalyst(onHistoryIdRemoved$);
  }

  private wireAnalystStateEnsured(): void {
    this.analysisStateStore.analystStateEnsured$
      .pipe(
        concatLatestFrom(() => [this.sampleId$, this.caseId$]),
        takeUntil(this.destroy$)
      )
      .subscribe(([analysisState, sampleId, caseId]) => {
        this.analystStore.addAnalyst({
          id: analysisState.analystId,
          historyId: analysisState.id,
          type: analysisState.analystEntity as 'user' | 'algorithm',
          name: analysisState.name || Parse.User.current()?.get('username') || '',
        });
        if (sampleId && caseId) {
          this.loadAnalystsForSample(sampleId, caseId);
        }
      });
  }

  // The URL's `historyId` queryParam is the source of truth for downstream data
  // fetching (findings, ROIs, etc. — see e.g. `fetchAssetFindings`). When
  // `resolveAnalystForSample` preserves a non-current-user selection across a
  // sample change, the URL was cleared by `navigate()` and stays empty unless
  // we push the new sample's historyId back into it. This subscription does
  // that — but it ONLY fires on STORE changes (via `withLatestFrom` on the
  // URL state) so that user-driven URL updates (`onAnalystChange` →
  // `navigateToUserAnalysis`) don't get rolled back by an out-of-date store
  // emission that arrives before `getHistory`/`getCurrentAnalyst` patches the
  // store to match.
  private wireAnalystUrlSync(): void {
    this.analystStore.selectedAnalystHistoryId$
      .pipe(
        switchMap((historyId) =>
          this.routerIdle$.pipe(
            filter((idle) => idle),
            take(1),
            map(() => historyId)
          )
        ),
        withLatestFrom(
          this.historyId$,
          this.analystStore.selectedAnalystIsCurrentUser$,
          this.sampleId$,
          this.caseId$,
          this.selectedAsset$
        ),
        filter(([selectedHistoryId, urlHistoryId, isCurrentUser, sampleId, caseId]) => {
          if (!sampleId || !caseId) return false;
          // Skip when there's no resolved selection yet (selectedAnalyst is
          // undefined → both selectedHistoryId and isCurrentUser are falsy).
          // Otherwise we'd clobber a deep-link/reload URL's historyId before
          // `resolveAnalystForSample` had a chance to honour it.
          if (!selectedHistoryId && !isCurrentUser) return false;
          const desired = isCurrentUser ? undefined : selectedHistoryId;
          return desired !== urlHistoryId;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([selectedHistoryId, , isCurrentUser, sampleId, caseId, selectedAsset]) => {
        // Build the path explicitly from store state instead of using
        // `relativeTo: this.route`. ActivatedRoute.snapshot can lag behind a
        // just-issued navigation, so a relative `[]` here was occasionally
        // pointing back to the previous sample/case and reverting an in-flight
        // breadcrumb change.
        const queryParams: Record<string, string> = {};
        if (selectedAsset?.id) queryParams.asset = selectedAsset.id;
        const desiredHistoryId = isCurrentUser ? undefined : selectedHistoryId;
        if (desiredHistoryId) queryParams.historyId = desiredHistoryId;
        this.router.navigate([`analyze/cases/${caseId}/images/${sampleId}`], {
          queryParams,
          replaceUrl: true,
        });
      });
  }

  private wireViewerModeOnHistoryChange(): void {
    this.historyId$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((historyId) => {
      if (this.isTechnicianOnly) {
        this.microLayout.setViewerMode(ViewerMode.idle);
        return;
      }
      combineLatest([this.aiAnalysisStatus$, this.selectedAnalyst$])
        .pipe(take(1))
        .subscribe(([status, selectedAnalyst]) => {
          if (algorithmAiAnalysisLocked(status, selectedAnalyst)) {
            this.microLayout.setViewerMode(ViewerMode.idle);
            return;
          }
          this.microLayout.setViewerMode(historyId ? ViewerMode.idle : ViewerMode.drawing);
        });
    });
  }

  private wireDataFetching(onAssetChanged$: Observable<string>, onStatsLoaded$: Observable<[boolean, boolean]>): void {
    // Trigger for asset-scoped data (findings/ROIs) based on the SELECTED ASSET
    // in state — not the URL `?asset` param. The URL is now updated via
    // `Location.replaceState` (to avoid competing navigations), which doesn't
    // update the router store, so the param-based trigger never fired on
    // landing and ROIs only appeared after picking another asset. The selected
    // asset state updates in every case (deep-link and in-app navigation).
    const onSelectedAssetChanged$ = this.selectedAsset$.pipe(
      map((asset) => asset?.id),
      filter((assetId): assetId is string => !!assetId),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // Findings/Analysis fetches are skipped for non-analyst roles (see the
    // `isAnalysisRole` gate in the constructor).
    if (this.isAnalysisRole) {
      combineLatest([
        this.ready$,
        onSelectedAssetChanged$,
        this.historyId$,
        this.sampleId$,
        this.changeRestorationComplete$,
        onStatsLoaded$,
      ])
        .pipe(
          distinctUntilChanged(),
          filter(
            ([ready, assetId, , sampleId, assetRestored, statsLoaded]) =>
              ready && !!assetId && !!sampleId && assetRestored && !!statsLoaded
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(([, assetId, historyId, sampleId]) => {
          this.fetchAssetFindings({
            sampleId,
            assetId,
            historyId,
          });
          this.deselectRois();
        });

      combineLatest([this.selectedAnalyst$, this.sampleId$])
        .pipe(
          distinctUntilChanged(),
          filter(([analyst, sampleId]) => !!analyst && !!sampleId),
          takeUntil(this.destroy$)
        )
        .subscribe(([analyst, sampleId]) => {
          this.fetchSampleFindings({ sampleId, analyst });
        });

      combineLatest([this.loading$, this.items$, this.selectedAnalyst$, this.sampleId$, this.findingsLoading$])
        .pipe(
          filter(
            ([loading, assets, analyst, sampleId, findingsLoading]) =>
              !loading &&
              !findingsLoading &&
              assets.length > 0 &&
              !!analyst &&
              !!sampleId &&
              assets[0]?.sampleId === sampleId
          ),
          distinctUntilChanged(
            (prev, curr) =>
              prev[3] === curr[3] &&
              prev[2]?.id === curr[2]?.id &&
              prev[0] === curr[0] &&
              prev[4] === curr[4] &&
              prev[1].length === curr[1].length &&
              prev[1][0]?.id === curr[1][0]?.id
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(([, , analyst, sampleId]) => {
          this.fetchReferenceStrip({ sampleId, analyst });
        });
    }

    combineLatest([this.loading$, this.items$, this.sampleId$, this.routerIdle$])
      .pipe(
        filter(
          ([loading, assets, sampleId, idle]) =>
            !loading && assets.length > 0 && !!sampleId && assets[0]?.sampleId === sampleId && idle
        ),
        distinctUntilChanged(
          (prev, curr) =>
            prev[2] === curr[2] && prev[1].length === curr[1].length && prev[1][0]?.assetFile === curr[1][0]?.assetFile
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([, assets, sampleId]) => {
        this.initializeSelectedAssetFromURL(assets, sampleId);
      });
  }

  private updateQueryParams(queryParams: any, sampleId?: string, caseId?: string): void {
    const routeSampleId = this.route.snapshot.paramMap.get('sampleid');
    const routeCaseId = this.route.snapshot.paramMap.get('caseid');

    const currentSampleId = sampleId || routeSampleId;
    const currentCaseId = caseId || routeCaseId;

    if (currentSampleId && currentCaseId) {
      this.router.navigate([`analyze/cases/${currentCaseId}/images/${currentSampleId}`], {
        queryParams,
        queryParamsHandling: 'merge',
      });
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
    }
  }

  private initializeSelectedAssetFromURL(sampleAssets: IAssetItem[], routeSampleId?: string): void {
    if (sampleAssets.length === 0) return;

    const assetId = this.route.snapshot.queryParams['asset'];
    const asset = sampleAssets.find((a) => a.id === assetId) ?? sampleAssets[0];

    // Guard against a stale asset list: when navigating between samples/cases
    // the PREVIOUS sample's assets can resolve right after we've already moved
    // on. Only act when the asset belongs to the route's current sample.
    if (routeSampleId && asset.sampleId && asset.sampleId !== routeSampleId) return;

    // Select the asset in state directly.
    this.selectAsset(of(asset.id));

    // Reflect the selection in the URL WITHOUT a router navigation. Using
    // `router.navigate` here issued a SECOND navigation to the (same) sample
    // right after the breadcrumb/sample navigation, and the two competed and
    // cancelled each other (and any late-resolving previous sample), breaking
    // every navigation after the first. `Location.replaceState` only rewrites
    // the address bar, so it can neither cancel nor be cancelled by the
    // in-flight route navigation.
    if (assetId !== asset.id) {
      const tree = this.router.createUrlTree([], {
        relativeTo: this.route,
        queryParams: { asset: asset.id },
        queryParamsHandling: 'merge',
      });
      this.location.replaceState(this.router.serializeUrl(tree));
    }
  }

  private readonly navigateToAnalyst = (analyst: Analyst | null) => {
    const assetId = this.route.snapshot.queryParamMap.get('asset');
    const queryParams: any = {
      ...(analyst?.historyId && { historyId: analyst.historyId }),
      ...(assetId && { asset: assetId }),
    };
    this.router.navigate([], { queryParams, relativeTo: this.route });
  };

  //Analyst and analysis states selectors
  public readonly userAnalysts$ = this.analystStore.userAnalysts$;
  public readonly aiAlgorithms$ = this.analystStore.aiAlgorithms$;
  public readonly selectedAnalyst$ = this.analystStore.selectedAnalyst$;
  public readonly isReviewingOtherAnalyst$ = this.analystStore.isReviewingOtherAnalyst$;
  public readonly analysisStates$ = this.analysisStateStore.analysisStates$;
  public readonly favouriteAssets$ = this.analysisStateStore.favouriteAssets$;

  public readonly findingsLoading$ = this.findingStore.loading$;

  private readonly analysisStatesLoading$ = this.select(
    this.analysisStates$,
    (analysisStates) => !analysisStates || analysisStates.size === 0
  );

  private readonly analystsLoading$ = this.select(
    this.analystStore.analysts$,
    this.analystStore.loading$,
    (analysts, loading) => loading || !analysts || analysts.length === 0
  );

  private readonly stateInfoFromSelectedAnalyst$ = this.select(
    this.selectedAnalyst$,
    this.analysisStates$,
    (selectedAnalyst, analysisStates) => {
      if (!selectedAnalyst) return null;
      return analysisStates.get(selectedAnalyst.id);
    }
  );

  public readonly currentAnalystFavouriteAssets$ = this.select(
    this.favouriteAssets$,
    this.stateInfoFromSelectedAnalyst$,
    (favouriteAssets, selectedAnalystState) => {
      return selectedAnalystState ? favouriteAssets.get(selectedAnalystState.id) || [] : [];
    }
  );

  /** Bookmarked asset of the selected analyst, sourced from the persisted analysis
   *  state so it survives reloads (mirrors currentAnalystFavouriteAssets$). */
  public readonly currentAnalystBookmarkedAsset$ = this.select(
    this.analysisStateStore.bookmarkedAssets$,
    this.stateInfoFromSelectedAnalyst$,
    (bookmarkedAssets, selectedAnalystState) => {
      return selectedAnalystState ? bookmarkedAssets.get(selectedAnalystState.id) ?? null : null;
    }
  );

  private readonly changeRestorationComplete$ = this.roiChangeStore.changeRestorationComplete$;
  public readonly excludedFromCounter$ = this.roiStatsStore.counterExcludedLabels$;

  public readonly editingEnabled$ = this.microLayout.editingEnabled$;

  public readonly aiAnalysisInProgress$ = this.sampleAiProgressStore.inProgress$;

  public readonly aiAnalysisStatus$ = this.sampleAiProgressStore.status$;

  public readonly aiAnalysisViewOnly$ = this.select(
    this.aiAnalysisStatus$,
    this.selectedAnalyst$,
    (status, selectedAnalyst) => algorithmAiAnalysisLocked(status, selectedAnalyst)
  );

  public readonly canEdit$ = this.select(
    this.selectedAnalyst$,
    this.editingEnabled$,
    this.aiAnalysisStatus$,
    (selectedAnalyst, editingEnabled, status) => {
      if (algorithmAiAnalysisLocked(status, selectedAnalyst)) {
        return false;
      }
      const isReviewingOtherAnalyst = selectedAnalyst ? selectedAnalyst.id !== Parse.User.current()?.id : false;
      return !isReviewingOtherAnalyst || editingEnabled;
    }
  );

  // Selectors
  public readonly ready$ = this.select(
    this.protocolStore.loading$,
    this.analystsLoading$,
    this.analysisStatesLoading$,
    (protocolLoading, analystsLoading, analysisStatesLoading) =>
      !protocolLoading && !analystsLoading && !analysisStatesLoading
  );

  private readonly error$ = this.select(
    this.protocolStore.protocolError$,
    this.analystStore.error$,
    this.assetStore.error$,
    this.roiStore.error$,
    this.analysisStateStore.error$,
    this.navigationStore.error$,
    (protocolError, analystError, assetError, roiError, stateError, navigationError) =>
      protocolError || analystError || assetError || roiError || stateError || navigationError
  );

  //Protocol selectors
  public readonly pipelines$ = this.protocolStore.pipelines$;
  public readonly steps$ = this.protocolStore.steps$;
  public readonly allOptions$ = this.protocolStore.allOptions$; // Unfiltered options for ROI overlays
  public readonly scopeOptions$ = this.protocolStore.options$; // Scope-filtered options for analysis panels
  private readonly allCategories$ = this.protocolStore.categories$;
  public readonly taggingCategories$ = this.protocolStore.taggingTasks$;
  public readonly segmentation$ = this.protocolStore.hasSegmentationTasks$;
  public readonly taggingProtocol$ = this.protocolStore.hasTaggingTasks$;
  public readonly loadingProtocol$ = this.protocolStore.loading$;
  public readonly acquisitionFields$ = this.protocolStore.acquisitionFields$;
  public readonly analysisContext$ = this.protocolStore.scope$;
  private readonly methodTypeId$ = this.protocolStore.methodTypeId$;
  public readonly mosaicEnabled$ = this.protocolStore.mosaicEnabled$; //TODO: combined selector that also checks that is not syncing

  //Navigation selectors.

  private readonly routeIds$ = this.globalStore.select(getRouterSelectors().selectRouteParams).pipe(
    map((params) => ({
      sampleId: (params?.['sampleid'] as string) ?? undefined,
      caseId: (params?.['caseid'] as string) ?? undefined,
    })),
    distinctUntilChanged((a, b) => a.sampleId === b.sampleId && a.caseId === b.caseId),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  public readonly sampleId$ = this.routeIds$.pipe(
    map(({ sampleId }) => sampleId),
    distinctUntilChanged()
  );
  public readonly caseId$ = this.routeIds$.pipe(
    map(({ caseId }) => caseId),
    distinctUntilChanged()
  );
  public readonly historyId$ = this.globalStore.select(
    getRouterSelectors().selectQueryParam('historyId')
  ) as Observable<string>;
  private readonly assetParam$ = this.globalStore.select(
    getRouterSelectors().selectQueryParam('asset')
  ) as Observable<string>;

  private readonly routerIdle$ = this.router.events.pipe(
    filter(
      (e) =>
        e instanceof NavigationStart ||
        e instanceof NavigationEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationError
    ),
    map((e) => !(e instanceof NavigationStart)),
    startWith(true),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  public readonly previousCase$ = this.navigationStore.previousCase$;
  public readonly nextCase$ = this.navigationStore.nextCase$;
  public readonly previousSample$ = this.navigationStore.previousSample$;
  public readonly nextSample$ = this.navigationStore.nextSample$;
  public readonly currentCase$ = this.navigationStore.currentCase$;
  public readonly currentSample$ = this.navigationStore.currentSample$;
  public readonly currentWorkspace$ = this.navigationStore.currentWorkspace$;

  // Breadcrumb navigation: selectors live in `NavigationStore`, fetching in the
  // component-provided `BreadcrumbNavigationEffects`, shared with the mosaic dashboard.
  // The dropdowns cascade, so the user drills workspace → case → sample and commits at
  // the sample — the deepest level here. Nothing resolves a "first sample of" anymore.
  public readonly currentCaseDisplay$ = this.navigationStore.currentCaseDisplay$;
  public readonly loadCasesForWorkspace = (workspaceId: string) =>
    this.breadcrumbNavigationEffects.loadCasesForWorkspace(of(workspaceId));
  public readonly loadSamplesForCase = (caseId: string) =>
    this.breadcrumbNavigationEffects.loadSamplesForCase(of(caseId));

  /** Navigate to a sample, choosing the viewer (images vs videos) from the
   *  sample's real asset type so a breadcrumb jump to a video sample opens the
   *  video viewer instead of staying on `images`. Defaults to `images`. */
  public readonly navigateToSample = (caseId: string, sampleId: string) => {
    if (!caseId || !sampleId) return;
    this.navigationStore.samplesByCaseId$.pipe(take(1)).subscribe((samplesByCaseId) => {
      const sample = samplesByCaseId?.[caseId]?.find((item) => item.id === sampleId);
      const viewer = sample?.isVideo === true ? 'videos' : 'images';
      this.router.navigate([`analyze/cases/${caseId}/${viewer}/${sampleId}`], {
        queryParams: {},
        replaceUrl: true,
      });
    });
  };

  private readonly currentUser$ = of(Parse.User.current() as Parse.User | undefined);
  public readonly currentUserAsAnalystOption$ = this.select(this.currentUser$, (user) =>
    user ? { id: user.id, name: (user.get('username') as string) ?? '', historyId: null, type: 'user' as const } : null
  );
  public readonly analystsExcludingCurrentUser$ = this.analystStore.analystsExcludingCurrentUser$;
  public readonly selectedAnalystIsCurrentUser$ = this.analystStore.selectedAnalystIsCurrentUser$;

  public readonly breadcrumbLevels$: Observable<NavigationBreadcrumbLevel[]> = combineLatest([
    this.navigationStore.breadcrumbLevels$,
    this.selectedAnalyst$,
    this.selectedAnalystIsCurrentUser$,
    this.currentUserAsAnalystOption$,
    this.analystsExcludingCurrentUser$,
    this.analysisStates$,
    this.aiAnalysisStatus$,
    this.translate.stream('core.me'),
    this.analystStore.analystsBySampleId$,
  ]).pipe(
    map(
      ([
        navigationLevels,
        selectedAnalyst,
        isCurrentUser,
        meOption,
        analysts,
        analysisStates,
        aiStatus,
        meLabel,
        analystsBySampleId,
      ]) => [
        // The analyst is this view's deepest level, so every sample in the cascade
        // expands into it: picking an analyst there commits sample and analyst at once.
        ...navigationLevels.map((level) => ({
          ...level,
          options: this.withAnalystChildren(level.options, analystsBySampleId, meLabel),
        })),
        // The "analyst" level (selecting whose analysis to view) is only
        // meaningful for analysis roles; a technician/technician-manager just
        // visualizes uploaded images, so the level is omitted for them.
        ...(this.authService.hasRoles(['admin', 'analyst', 'analystmanager'])
          ? [
              {
                key: 'analyst',
                labelKey: 'core.analyst',
                icon: 'ri-user-line',
                current: selectedAnalyst
                  ? {
                      id: selectedAnalyst.id,
                      name: isCurrentUser ? meLabel : selectedAnalyst.name,
                      indicator: MicroscopeImageFacade.toAiIndicator(selectedAnalyst, aiStatus),
                    }
                  : null,
                options: [
                  ...(meOption ? [{ id: meOption.id, name: meLabel }] : []),
                  ...(analysts ?? []).map((analyst) => this.toAnalystOption(analyst, analysisStates, aiStatus)),
                ],
              },
            ]
          : []),
      ]
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private withAnalystChildren(
    options: NavigationBreadcrumbOption[] | null | undefined,
    analystsBySampleId: Record<string, Analyst[] | null>,
    meLabel: string
  ): NavigationBreadcrumbOption[] | null {
    if (!options) return options ?? null;

    // Technicians navigate sample/case only; no analyst sub-selection in the dropdown.
    if (!this.authService.hasRoles(['admin', 'analyst', 'analystmanager'])) return options;

    return options.map((option) => {
      if (option.levelKey !== 'sample') {
        return { ...option, children: this.withAnalystChildren(option.children, analystsBySampleId, meLabel) };
      }

      const analysts = analystsBySampleId[option.id];
      const currentUserId = Parse.User.current()?.id;
      const children = analysts
        ? [
            ...(currentUserId ? [{ id: currentUserId, name: meLabel, levelKey: 'analyst' }] : []),
            ...analysts
              .filter((analyst) => analyst.id !== currentUserId)
              .map((analyst) => ({
                id: analyst.id,
                name: analyst.name,
                levelKey: 'analyst',
                badge: analyst.type === 'algorithm' ? 'AI' : undefined,
              })),
          ]
        : null;

      return { ...option, expandable: true, children };
    });
  }

  private toAnalystOption(
    analyst: Analyst,
    analysisStates: Map<string, { updatedAt?: Date }> | null,
    aiStatus: string | undefined
  ): NavigationBreadcrumbOption {
    const updatedAt = analysisStates?.get(analyst.id)?.updatedAt;
    return {
      id: analyst.id,
      name: analyst.name,
      badge: analyst.type === 'algorithm' ? 'AI' : undefined,
      hint: updatedAt ? formatDate(updatedAt, 'dd/MM/yyyy, hh:mm a', this.locale) : undefined,
      indicator: MicroscopeImageFacade.toAiIndicator(analyst, aiStatus),
    };
  }

  /** AI-progress dot for algorithm analysts: green once the analysis is ready,
   *  orange while it runs — identical to the mosaic dashboard's indicator. */
  private static toAiIndicator(
    analyst: Analyst,
    aiStatus: string | undefined
  ): NavigationBreadcrumbIndicator | undefined {
    if (analyst.type !== 'algorithm' || !aiStatus) return undefined;
    return {
      kind: aiStatus === 'ready' ? 'success' : 'in-progress',
      tooltip: aiStatus === 'ready' ? 'analyzed' : aiStatus,
    };
  }

  /** Analyst-level dropdown opened: load this sample's analysts and their states. */
  public readonly loadAnalysts = (sampleId: string) => this.analystStore.loadAnalysts(sampleId);
  public readonly loadAnalysisStates = (sampleId: string, caseId: string) =>
    this.analysisStateStore.fetchAnalysisStates({ sampleId, caseId });
  /** Cascade: a sample row was expanded — load its analysts for the sub-menu. */
  public readonly loadAnalystsForSampleId = (sampleId: string) =>
    this.analystStore.loadAnalystsForSampleId(of(sampleId));

  /** Set by `openSampleAsAnalyst`, consumed by `loadAnalystsForSample` on arrival. */
  private pendingAnalystSelection: { sampleId: string; analystId: string } | null = null;

  public readonly openSampleAsAnalyst = (caseId: string, sampleId: string, analystId: string) => {
    if (!caseId || !sampleId || !analystId) return;
    this.navigationStore.samplesByCaseId$.pipe(take(1)).subscribe((samplesByCaseId) => {
      // Video samples have no analyses — never route them through the analyst
      // flow (which would keep the `images` route); open the video viewer instead.
      const isVideo = samplesByCaseId?.[caseId]?.find((item) => item.id === sampleId)?.isVideo === true;
      if (isVideo) {
        this.router.navigate([`analyze/cases/${caseId}/videos/${sampleId}`], {
          queryParams: {},
          replaceUrl: true,
        });
        return;
      }
      this.pendingAnalystSelection = { sampleId, analystId };
      const historyId =
        analystId === Parse.User.current()?.id
          ? undefined
          : this.analystStore.analystsForSample(sampleId)?.find((analyst) => analyst.id === analystId)?.historyId;
      this.router.navigate([`analyze/cases/${caseId}/images/${sampleId}`], {
        queryParams: historyId ? { historyId } : {},
      });
    });
  };

  public readonly selectAnalyst = (analyst: Analyst) => {
    if (analyst.id === Parse.User.current()?.id) {
      this.selectOwnAnalysis();
      return;
    }
    this.analystStore.selectAnalyst(analyst.id);
  };

  /** Switch to the current user's own analysis; get-or-creates its state if none exists yet. */
  public readonly selectOwnAnalysis = () =>
    this.analystStore.getCurrentAnalyst(
      this.sampleId$.pipe(
        filter((sampleId): sampleId is string => !!sampleId),
        take(1)
      )
    );

  // Drives the viewer's loading overlay. It must cover ONLY a sample/case/
  // workspace navigation — from the moment the sampleId changes until the new
  // image is actually displayed (the `opened` event of the viewer, which is
  // the slow part). Asset switching within a sample must NOT trigger it.
  // A plain subject avoids the flicker we got from OR-ing two independent
  // loading flags (asset list + findings) that toggle at different times.
  private readonly navigationLoadingSubject$ = new BehaviorSubject<boolean>(false);

  // True when the image we navigated to failed to load (e.g. its DZI `/info`
  // 404s). Driven imperatively by `onImageLoadError` for the fetch-failure
  // case. The missing-dimensions case is handled reactively below.
  private readonly navigationErrorSubject$ = new BehaviorSubject<boolean>(false);

  // Derived, replay-backed signal: the selected asset is loaded but has no
  // registered width/height, so there is no pyramid to show. Deriving this
  // reactively — instead of reacting to the one-shot `assetInfoError$` event —
  // removes a timing race on first navigation: when the app enters WITHOUT an
  // `asset` query-param and auto-selects the first asset, the event could fire
  // in the same tick the selection resolved, so the overlay never showed and
  // the viewer sat on a black canvas until the 20s safety timeout. A derived
  // selector always reflects the current selection regardless of emit order.
  private readonly selectedAssetMissingDimensions$ = combineLatest([
    this.assetStore.selectedAsset$,
    this.assetStore.loading$,
  ]).pipe(
    map(([asset, loading]) => !loading && !!asset && (!asset.data?.width || !asset.data?.height)),
    distinctUntilChanged()
  );

  // Drives the viewer's loading overlay. Suppressed once we know the selected
  // asset has no dimensions (there is nothing to wait for — show the error).
  public readonly navigationLoading$ = combineLatest([
    this.navigationLoadingSubject$,
    this.selectedAssetMissingDimensions$,
  ]).pipe(
    map(([loading, missingDimensions]) => loading && !missingDimensions),
    distinctUntilChanged()
  );

  // The overlay shows "image unavailable" when either the pyramid fetch failed
  // (imperative subject) or the selected asset has no registered dimensions.
  public readonly navigationError$ = combineLatest([
    this.navigationErrorSubject$,
    this.selectedAssetMissingDimensions$,
  ]).pipe(
    map(([error, missingDimensions]) => error || missingDimensions),
    distinctUntilChanged()
  );

  private navLoadingTimeout?: ReturnType<typeof setTimeout>;

  private startNavigationLoading(): void {
    this.navigationErrorSubject$.next(false);
    this.navigationLoadingSubject$.next(true);
    // Safety net: never leave the full-screen overlay stuck if neither the
    // viewer's `opened` event nor an error ever arrive.
    clearTimeout(this.navLoadingTimeout);
    this.navLoadingTimeout = setTimeout(() => this.navigationLoadingSubject$.next(false), 20000);
  }

  /** Called by the component when the viewer finishes opening an image. */
  public onImageOpened(): void {
    clearTimeout(this.navLoadingTimeout);
    this.navigationErrorSubject$.next(false);
    this.navigationLoadingSubject$.next(false);
  }

  /** Called when the navigated-to image fails to load — keep the overlay up
   *  but switch it from spinner to an "unavailable" message. */
  private onImageLoadError(): void {
    clearTimeout(this.navLoadingTimeout);
    this.navigationErrorSubject$.next(true);
    this.navigationLoadingSubject$.next(false);
  }

  // Label currently hovered in the analysis panel. ROIs carrying it get
  // highlighted in the viewer (others dimmed). Null when nothing is hovered.
  private readonly hoveredLabelSubject$ = new BehaviorSubject<string | null>(null);
  public readonly hoveredLabel$ = this.hoveredLabelSubject$.pipe(distinctUntilChanged());

  public setHoveredLabel(labelId: string | null): void {
    this.hoveredLabelSubject$.next(labelId);
  }

  //Selectors from asset store
  private readonly loading$ = this.assetStore.loading$;
  public readonly items$ = this.assetStore.items$;
  public readonly selectedAsset$ = this.assetStore.selectedAsset$;
  public readonly selectedIndex$ = this.assetStore.selectedIndex$;
  public readonly totalAssets$ = this.assetStore.totalAssets$;
  private readonly canNavigateNext$ = this.assetStore.canNavigateNext$;
  private readonly canNavigatePrevious$ = this.assetStore.canNavigatePrevious$;

  public readonly tileSource$ = this.assetStore.tileSource$;
  public readonly focusPoint$ = this.assetStore.focusPoint$;

  public readonly stitchingScans$ = this.assetStore.stitchingScans$;
  public readonly hspan$ = this.assetStore.hspan$;
  public readonly bookmarkedAsset$ = this.currentAnalystBookmarkedAsset$;
  public readonly dataEditable$ = this.assetStore.dataEditable$;
  public readonly updateAssetDataField = this.assetStore.updateAssetDataField;

  //Selectors from micro-layout
  public readonly acquisitionEdition$ = this.microLayout.acquisitionEdition$;
  public readonly collapsedCategories$ = this.microLayout.collapsedCategories$;
  public readonly multiSelection$ = this.microLayout.multiSelection$;
  public readonly hiddenLabels$ = this.microLayout.hiddenLabels$;
  public readonly pinnedLabels$ = this.microLayout.pinnedLabels$;
  public readonly viewerMode$ = this.microLayout.viewerMode$;
  public readonly protocolPanelCollapsed$ = this.microLayout.protocolPanelCollapsed$;

  /** Collapse/expand the protocol side panel (compact colours+numbers rail). */
  public toggleProtocolPanel(): void {
    this.microLayout.toggleProtocolPanel();
  }

  public readonly statsMode$ = this.select(this.roiStatsStore.counterExclusionMode$, (mode) =>
    mode === 'selective' ? StatsMode.SELECTIVE : StatsMode.COMPLETE
  );

  public readonly drawingMode$ = this.microLayout.drawingMode$;
  public readonly tabActiveContext$ = this.microLayout.tabContext$;
  public readonly selectedPreset$ = this.microLayout.selectedPreset$;
  public readonly cursor$ = this.microLayout.cursorSelector$;
  public readonly showPinned$ = this.microLayout.showPinnedOnly$;
  public readonly availableDrawingModes$ = this.microLayout.availableDrawingModes$;
  public readonly imageFilters$ = this.microLayout.imageFilters$;
  public readonly rulerActive$ = this.microLayout.rulerActive$;
  public readonly rulerIcon$ = this.microLayout.rulerType$;
  public readonly rulerSize$ = this.select(this.microLayout.rulerSize$, this.assetStore.hspan$, (rulerSize, hspan) => {
    if (rulerSize != null) return rulerSize;
    if (hspan != null) return Math.round((hspan * 100) / 5) * 5;
    return null;
  });
  public readonly assetFilters$ = this.microLayout.assetFilters$;
  public readonly viewedAssets$ = this.microLayout.viewedAssets$.pipe(map((assets) => Array.from(assets)));
  public readonly reviewedAssets$ = this.microLayout.reviewedAssets$.pipe(map((assets) => Array.from(assets)));
  public readonly analyzedAssets$ = this.microLayout.analyzedAssets$.pipe(map((assets) => Array.from(assets)));
  public readonly getProgress$ = this.select(this.viewedAssets$, this.items$, (viewedAssets, items) => {
    const total = items?.length ?? 0;
    if (total === 0) return 0;
    const sampleAssetIds = new Set(items.map((item) => item.id));
    const viewedInSample = viewedAssets.filter((id) => sampleAssetIds.has(id)).length;
    return Math.min(100, Math.round((viewedInSample / total) * 100));
  });
  public readonly selectedLabels$ = this.select(
    this.scopeOptions$,
    this.protocolStore.pipelines$,
    this.protocolStore.categories$,
    this.microLayout.selectedLabels$,
    (options, pipelines, categories, selectedLabelIds) => {
      if (!options || !categories) return [];

      if (!selectedLabelIds || selectedLabelIds.size === 0) return [];

      const labels = Object.values(options)
        .filter((option) => selectedLabelIds.has(option.id))
        .map((option) => {
          const category = Object.values(categories).find((cat) => cat.options.includes(option.id));
          const pipeline = pipelines.find((pl) => pl.categories.includes(category?.id));
          return {
            ...option,
            category: category?.name,
            stepId: category?.stepId,
            pipelineId: pipeline?.id,
          };
        });

      return labels;
    }
  );

  public readonly filteredItems$ = this.select(
    this.microLayout.assetFilters$,
    this.analyzedAssets$,
    this.items$,
    this.favouriteAssets$,
    this.stateInfoFromSelectedAnalyst$,
    (assetFilters, analyzedAssets, items, favouriteAssets, selectedAnalystState) => {
      if (!assetFilters.analyzed && !assetFilters.favAcquisition && !assetFilters.favAnalysis) {
        return items;
      }

      const selectedAnalystFavAssets = selectedAnalystState ? favouriteAssets.get(selectedAnalystState.id) || [] : [];
      const analyzedAssetsSet = new Set(analyzedAssets);

      return items.filter((item) => {
        const isAnalyzed = analyzedAssetsSet.has(item.id);
        const isFavAcquisition = item.fav || false;
        const isFavAnalysis = selectedAnalystFavAssets.includes(item.id);

        let matches = true;

        if (assetFilters.analyzed) {
          matches = matches && isAnalyzed;
        }
        if (assetFilters.favAcquisition) {
          matches = matches && isFavAcquisition;
        }
        if (assetFilters.favAnalysis) {
          matches = matches && isFavAnalysis;
        }

        return matches;
      });
    }
  );

  public readonly availableViewerModes$ = this.select(
    this.protocolStore.hasTaggingTasks$,
    this.protocolStore.hasSegmentationTasks$,
    (hasROITasks, hasSegmTasks) => {
      if (this.isTechnicianOnly) return [ViewerMode.idle];
      return [
        ViewerMode.idle,
        ...(hasROITasks ? [ViewerMode.drawing, ViewerMode.selecting] : []),
        ...(hasSegmTasks ? [ViewerMode.free] : []),
      ];
    }
  );

  public readonly selectedToolIndex$ = this.select(
    this.availableViewerModes$,
    this.viewerMode$,
    (availableModes, activeMode) => (availableModes.includes(activeMode) ? availableModes.indexOf(activeMode) : null)
  );

  public readonly visibleLabels$ = this.select(this.scopeOptions$, this.hiddenLabels$, (labels, hiddenLabels) => {
    if (!labels) return [];
    return Object.keys(labels).filter((labelId) => !hiddenLabels.has(labelId));
  });

  public readonly visibleOptions$ = this.select(
    this.scopeOptions$,
    this.showPinned$,
    this.pinnedLabels$,
    (labels, showOnlyPinned, pinnedLabels) => {
      if (!labels) return {};

      if (!showOnlyPinned) return labels;

      return Object.fromEntries(Object.entries(labels).filter(([key]) => pinnedLabels.has(key)));
    }
  );

  public readonly visibleCategories$ = this.select(
    this.allCategories$,
    this.showPinned$,
    this.pinnedLabels$,
    (categories, showOnlyPinned, pinnedLabels) => {
      if (!categories) return {};

      if (!showOnlyPinned) return categories;

      return Object.fromEntries(
        Object.entries(categories).filter(([, category]) =>
          category.options?.some((optionId: string) => pinnedLabels.has(optionId))
        )
      );
    }
  );

  public readonly activeAssetFindings$ = this.findingEffects.activeAssetFindings$;
  public readonly activeSampleFindings$ = this.findingEffects.activeSampleFindings$;
  public readonly activeFindings$ = this.findingEffects.activeFindings$;
  public readonly activeRoiFindings$ = this.findingEffects.activeRoiFindings$;

  public readonly unsynced$ = this.select(
    this.selectedAnalyst$,
    this.roiChangeStore.unsynced$,
    this.findingStore.changedFindings$,
    this.findingStore.findings$,
    (selectedAnalyst, unsyncedRois, changedFindings, findings) => {
      if (!selectedAnalyst) return false;

      const analystId = selectedAnalyst.id;
      const analystEntity = selectedAnalyst.type;

      const hasFindingChanges = Array.from(changedFindings.keys()).some((findingUuid) => {
        const finding = findings?.[findingUuid];
        return finding?.creatorId === analystId && finding?.creatorEntity === analystEntity;
      });

      return unsyncedRois || hasFindingChanges;
    }
  );

  public readonly syncing$ = this.select(
    this.findingStore.syncingChanges$,
    this.roiChangeStore.syncing$,
    (findingSyncing, roiSyncing) => findingSyncing || roiSyncing
  );

  public readonly applyingChanges$ = this.roiChangeStore.applyingChanges$;
  public readonly revertingChanges$ = this.roiChangeStore.revertingChanges$;

  public readonly busy$ = this.select(
    this.syncing$,
    this.applyingChanges$,
    this.revertingChanges$,
    (syncing, applyingChanges, revertingChanges) => syncing || applyingChanges || revertingChanges
  );

  public readonly editingActive$ = this.select(
    this.canEdit$,
    this.syncing$,
    this.roiChangeStore.applyingChanges$,
    this.roiChangeStore.revertingChanges$,
    (canEdit, syncing, applyingChanges, revertingChanges) =>
      canEdit && !syncing && !applyingChanges && !revertingChanges
  );

  public readonly changesRestored$ = this.roiChangeStore.changesRestored$;
  private readonly rois$ = this.roiStore.rois$;
  public readonly selectedRois$ = this.select(
    this.microLayout.selectedRois$,
    this.rois$,
    this.selectedAsset$,
    (selectedRois, rois, selectedAsset) => {
      const filtered = new Set<string>();
      const assetId = selectedAsset?.id;
      selectedRois.forEach((roiId) => {
        const roi = rois?.[roiId];
        if (roi && roi.labels.size > 0 && (!assetId || roi.assetId === assetId)) {
          filtered.add(roiId);
        }
      });
      return filtered;
    }
  );

  private readonly visibleRois$ = this.select(
    this.rois$,
    this.selectedAsset$,
    this.hiddenLabels$,
    this.allOptions$,
    (rois, selectedAsset, hiddenLabels, allOptions) => {
      if (!selectedAsset?.id || !rois || Object.keys(rois).length === 0) {
        return [];
      }

      return Object.values(rois).filter((roi) => {
        if (roi.assetId !== selectedAsset.id) {
          return false;
        }

        const roiLabels = Array.from(roi.labels.entries());

        return roiLabels.some(([labelId, score]) => {
          if (hiddenLabels.has(labelId)) {
            return false;
          }

          const labelOption = allOptions[labelId];
          const threshold = labelOption?.threshold || 0;
          return score >= threshold;
        });
      });
    }
  );

  public readonly activeAssetRoiCountsByLabel$ = this.roiStatsEffects.activeAssetRoiCountsByLabel$;
  public readonly totalActiveAssetRoiCount$ = this.roiStatsEffects.totalActiveAssetRoiCount$;
  public readonly assetCategoryCounts$ = this.roiStatsEffects.assetCategoryCounts$;

  public readonly totalActiveDetections$ = this.roiStatsStore.totalActiveDetections$;
  public readonly activeLabelCounts$ = this.roiStatsStore.activeLabelCounts$;
  public readonly totalDetections$ = this.roiStatsStore.totalDetections$;

  public readonly roiOverlays$ = this.select(
    this.visibleRois$,
    this.selectedAsset$,
    this.allOptions$,
    this.selectedRois$,
    (rois, selectedAsset, allOptions, selectedRois) => {
      if (!selectedAsset || !rois.length) return [];

      const assetWidth = selectedAsset.data?.width || 1;
      const assetHeight = selectedAsset.data?.height || 1;

      return rois.map((roi) => {
        const x = roi.coordinates.x0 * assetWidth;
        const y = roi.coordinates.y0 * assetHeight;
        const w = (roi.coordinates.x1 - roi.coordinates.x0) * assetWidth;
        const h = (roi.coordinates.y1 - roi.coordinates.y0) * assetHeight;

        const firstLabelId = Array.from(roi.labels.keys())[0];
        const firstLabel = allOptions[firstLabelId];
        const color = firstLabel?.color;

        const selected = selectedRois?.has(roi.id) || false;

        const labelNames = Array.from(roi.labels.keys())
          .map((labelId) => allOptions[labelId]?.name || '')
          .filter((name) => name)
          .join(', ');

        const tooltip = labelNames || '(no tags)';

        return {
          id: roi.id,
          x,
          y,
          w,
          h,
          color,
          selected,
          tooltip,
        } as OverlayData;
      });
    }
  );

  // IDs of the ROIs that carry the hovered label. Kept SEPARATE from
  // `roiOverlays$` so hovering only restyles existing overlay elements (cheap)
  // instead of recomputing + re-rendering every overlay.
  public readonly highlightedRoiIds$ = this.select(this.visibleRois$, this.hoveredLabel$, (rois, hoveredLabel) => {
    if (!hoveredLabel) return new Set<string>();
    return new Set(rois.filter((roi) => roi.labels.has(hoveredLabel)).map((roi) => roi.id));
  });

  // Label OBJECTS (id/name/color/...) present in the currently selected ROIs,
  // for the quick-remove chip panel shown when a ROI is selected. Built the
  // same way as `selectedLabels$` but sourced from the selection's labels.
  public readonly selectedRoiLabels$ = this.select(
    this.scopeOptions$,
    this.protocolStore.pipelines$,
    this.protocolStore.categories$,
    this.contextMenuEffects.selectedItemLabels$,
    (options, pipelines, categories, selectedItemLabelIds) => {
      if (!options || !categories || !selectedItemLabelIds?.length) return [];

      const idSet = new Set(selectedItemLabelIds);

      return Object.values(options)
        .filter((option) => idSet.has(option.id))
        .map((option) => {
          const category = Object.values(categories).find((cat) => cat.options.includes(option.id));
          const pipeline = pipelines.find((pl) => pl.categories.includes(category?.id));
          return {
            ...option,
            category: category?.name,
            stepId: category?.stepId,
            pipelineId: pipeline?.id,
          };
        });
    }
  );

  /** Remove a single label from every selected ROI (the chip ✕). */
  public readonly removeSelectedRoiLabel = this.effect((labelId$: Observable<string>) =>
    labelId$.pipe(
      concatLatestFrom(() => this.selectedRois$),
      tap(([labelId, selectedRois]) => {
        if (selectedRois.size === 0) return;
        this.removeLabels(Array.from(selectedRois), [labelId]);
      })
    )
  );

  /** Remove all labels from the selected ROIs (the chip panel "clear"). */
  public readonly clearSelectedRoiLabels = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => this.selectedRois$),
      tap(([, selectedRois]) => {
        if (selectedRois.size === 0) return;
        this.removeAllLabels(selectedRois);
      })
    )
  );

  public readonly currentBoxDimensions$ = this.microLayout.createCurrentBoxDimensionsSelector(this.selectedLabels$);

  public setProtocolContext(context: ProtocolScope) {
    this.protocolStore.setScope(context);
  }

  private fetchProtocol(sampleId: string) {
    this.protocolStore.selectSample(of(sampleId));
  }

  private loadSampleAssets(sampleId: string) {
    this.assetStore.loadSampleAssets(of(sampleId));
  }

  public readonly navigateNext = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.selectedIndex$, this.items$, this.canNavigateNext$]),
      tap(([, selectedIndex, items, canNavigate]) => {
        if (!canNavigate) return;

        const nextAsset = items.find((item) => item.index === selectedIndex + 1);
        if (nextAsset) {
          this.selectAsset(of(nextAsset.id));
          this.updateQueryParams({ asset: nextAsset.id });
        }
      }),
      catchError((error) => {
        console.error(`Failed to navigate to next asset: ${error.message}`);
        return EMPTY;
      })
    )
  );

  public readonly navigatePrevious = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.selectedIndex$, this.items$, this.canNavigatePrevious$]),
      tap(([, selectedIndex, items, canNavigate]) => {
        if (!canNavigate) return;

        const prevAsset = items.find((item) => item.index === selectedIndex - 1);
        if (prevAsset) {
          this.selectAsset(prevAsset.id);
          this.updateQueryParams({ asset: prevAsset.id });
        }
      }),
      catchError((error) => {
        console.error(`Failed to navigate to previous asset: ${error.message}`);
        return EMPTY;
      })
    )
  );

  public readonly navigateToIndex = this.effect((index$: Observable<number>) =>
    index$.pipe(
      concatLatestFrom(() => this.items$),
      tap(([index, items]) => {
        if (index < 0 || index >= items.length) return;

        const asset = items.find((item) => item.index === index);
        if (asset) {
          this.selectAsset(asset.id);
          this.updateQueryParams({ asset: asset.id });
        }
      }),
      catchError((error) => {
        console.error(`Failed to navigate to index: ${error.message}`);
        return EMPTY;
      })
    )
  );
  public saveAssetData() {
    this.assetStore.saveAssetData(of(void 0));
  }
  public discardDataChanges() {
    this.assetStore.discardDataChanges(of(void 0));
  }

  private discardChanges(): void {
    combineLatest([
      this.sampleId$,
      this.selectedAnalyst$,
      this.roiChangeStore.changedRois$,
      this.findingStore.changedFindings$,
    ])
      .pipe(take(1))
      .subscribe(([sampleId, selectedAnalyst, changedRois, changedFindings]) => {
        if (!sampleId || !selectedAnalyst?.id) return;
        if (!Parse.User.current()?.id) return;

        const changedRoiIds = Array.from(changedRois.keys());
        if (changedRoiIds.length > 0) {
          this.roiRevertEffects.revertAllChanges({ sampleId, creatorId: selectedAnalyst.id });
        }
        const changedFindingIds = Array.from(changedFindings.keys());
        if (changedFindingIds.length > 0) {
          this.findingStore.revertChanges(changedFindingIds);
        }
      });
  }

  public setViewerMode(mode: ViewerMode) {
    this.microLayout.setViewerMode(mode);
  }

  public resetRuler() {
    this.microLayout.resetRuler();
  }

  public updateImageFilters(data) {
    this.microLayout.updateImageFilters(data);
  }

  public clearSelectedLabels() {
    this.microLayout.clearSelectedLabels();
  }

  public addViewedAsset(assetId: string) {
    this.microLayout.addViewedAsset(assetId);
  }

  public updateAssetFilters(filter: 'analyzed' | 'favAcquisition' | 'favAnalysis') {
    this.microLayout.updateAssetFilters(filter);
  }

  public toggleMultiSelection() {
    this.microLayout.toggleMultiSelection();
  }

  public togglePinnedOptions() {
    this.microLayout.toggleShowPinnedOnly();
  }

  public selectRois(roiIds: string[]) {
    this.microLayout.selectRois(roiIds);
  }

  public deselectRois() {
    this.microLayout.deselectRois();
  }

  public resetImageFilters() {
    this.microLayout.resetImageFilters();
  }

  public changeRulerIcon(type: RulerType) {
    this.microLayout.setRulerType(type);
  }
  public changeRulerSize(size: number) {
    this.microLayout.setRulerSize(size);
  }
  public collapseCategory(label: string) {
    this.microLayout.toggleCollapsedCategory(label);
  }
  public onPinnedLabel(label: string) {
    this.microLayout.togglePinnedLabel(label);
  }

  public onVisibleLabel(label: string) {
    this.microLayout.toggleHiddenLabel(label);
  }

  public setStatsMode(mode: StatsMode) {
    this.roiStatsEffects.toggleCounterExclusionMode(mode === StatsMode.SELECTIVE ? 'selective' : 'complete');
  }

  public setTabContext(ctx: ETabContext) {
    this.microLayout.setTabContext(ctx);
  }
  public setDrawingMode(mode) {
    this.microLayout.setDrawingMode(mode);
  }
  public updateImageFilter(filter: ImageFilter) {
    this.microLayout.updateImageFilter(filter);
  }
  public toogleAcquisitionEdition(value: boolean) {
    this.microLayout.setAcquisitionEdition(value);
  }

  public readonly toggleEditing = this.effect<boolean>((value$) =>
    value$.pipe(
      concatLatestFrom(() => [this.syncing$, this.aiAnalysisStatus$, this.selectedAnalyst$]),
      filter(([, syncing]) => !syncing),
      tap(([value, , status, selectedAnalyst]) => {
        const viewOnly = algorithmAiAnalysisLocked(status, selectedAnalyst);
        const enabled = viewOnly ? false : value;
        this.microLayout.toggleEditing(enabled);
        this.setViewerMode(enabled ? ViewerMode.drawing : ViewerMode.idle);
      })
    )
  );

  public selectLabel(labelUuid: string, replace = false) {
    this.microLayout.selectLabel({ uuid: labelUuid, replace });
  }
  private fetchNavigationReferences(sampleId: string) {
    this.breadcrumbNavigationEffects.fetchNavigationReferences(sampleId);
  }

  private navigateToCaseDetail(): void {
    this.caseId$.pipe(take(1)).subscribe((caseId) => {
      this.router.navigateByUrl(`/analyze/cases/${caseId}`);
    });
  }

  public initializeBoxDimensions(labels: any[]): void {
    this.microLayout.initializeBoxDimensions(labels);
  }

  public removeAllLabels(roiIds: Set<string>) {
    this.roiLabelingEffects.removeAllLabels(Array.from(roiIds));
    this.microLayout.deselectRois();
  }

  public removeLabels(roiIds: string[], labels: string[]) {
    this.roiLabelingEffects.removeLabels({ roiIds, labels });
  }

  // --- Dialog methods ---

  public openReviewSampleDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.review_sample',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.continue',
      },
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => !(result !== 'accept' && result !== 'default'))
      )
      .subscribe(() => {
        this.runSaveActionIfAllowed(() => {
          this.doSyncChanges();

          if (this.authService.currentUser.isOnlyGuest) return;
          this.updateCaseState(IState.INPROGRES);
          this.updateSampleState(IState.INPROGRES);
        });
      });
  }

  public openAcceptCompleteAnalysisDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.accept_complete_analysis',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.accept_complete_analysis',
      },
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => !(result !== 'accept' && result !== 'default'))
      )
      .subscribe(() => {
        this.runSaveActionIfAllowed(() => {
          this.doSyncChanges();

          if (this.authService.currentUser.isOnlyGuest) return;
          this.updateCaseState(IState.INPROGRES);
          this.updateSampleState(IState.INPROGRES);
        });
      });
  }

  public openDiscardChangesDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.discard_changes_prompt',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.discard_changes',
        defaultAction: 'cancel',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => result === 'accept' || result === 'default')
      )
      .subscribe(() => {
        this.discardChanges();
      });
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.exit_without_saving_prompt',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.save',
        dontSaveButtonText: 'button.continue',
        defaultAction: 'accept',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    const onDialogClose = this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((answer) => answer !== 'cancel')
      );

    onDialogClose.pipe(filter((answer: string) => answer === 'discard')).subscribe(() => this.navigateToCaseDetail());

    onDialogClose
      .pipe(
        filter((answer: string) => answer === 'accept'),
        concatLatestFrom(() => this.sampleId$)
      )
      .subscribe(([, sampleId]) => {
        const currentAnalystId = Parse.User.current()?.id;
        if (sampleId && currentAnalystId) {
          this.runSaveActionIfAllowed(() => {
            this.doSyncChanges();

            if (this.authService.currentUser.isOnlyGuest) return;
            this.updateCaseState(IState.INPROGRES);
            this.updateSampleState(IState.INPROGRES);
          });
        }
      });
  }

  public openUnauthorizedDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'error.session_expired',
        acceptButtonText: 'button.login',
        defaultAction: 'accept',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.router.navigate(['logout']));
  }

  public openSnackbar(message: string): void {
    this.snackbarService.show(message);
  }

  // --- Orchestration methods ---

  public readonly save = this.effect<void>((trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.isReviewingOtherAnalyst$, this.busy$]),
      tap(([, isReviewingOtherAnalyst, busy]) => {
        if (busy) return;

        if (isReviewingOtherAnalyst) {
          this.openReviewSampleDialog();
        } else {
          this.doSyncChanges();

          if (this.authService.currentUser.isOnlyGuest) return;
          this.updateCaseState(IState.INPROGRES);
          this.updateSampleState(IState.INPROGRES);
        }
      })
    )
  );

  public acceptCompleteAnalysis(): void {
    this.isReviewingOtherAnalyst$.pipe(take(1)).subscribe((isReviewingOtherAnalyst) => {
      if (isReviewingOtherAnalyst) {
        this.openAcceptCompleteAnalysisDialog();
      }
    });
  }

  public exit(): void {
    this.unsynced$.pipe(take(1)).subscribe((unsynced) => {
      if (unsynced) {
        this.openUnsavedChangesDialog();
      } else {
        this.navigateToCaseDetail();
      }
    });
  }

  // --- Reactive wiring for snackbars and error handling ---

  public canDeactivate(): Observable<boolean> {
    return this.busy$.pipe(
      take(1),
      tap((busy) => {
        if (busy) this.snackbarService.show('info.wait_for_changes_to_finish');
      }),
      map((busy) => !busy)
    );
  }

  private wireReactiveEffects(): void {
    this.unsynced$.pipe(takeUntil(this.destroy$)).subscribe((unsynced) => (this.hasUnsyncedChanges = unsynced));

    this.error$
      .pipe(
        filter((value) => value === ApiAuthErrors.UNAUTHORIZED),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.openUnauthorizedDialog());

    this.changesRestored$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        filter((value) => !!value),
        debounceTime(this.ALERT_DEBOUNCE_TIME)
      )
      .subscribe(() => this.openSnackbar('analysis.changes_restored'));

    // "Synced" snackbar: fires on the falling edge of `syncing$`, i.e. right
    // after a sync (or a review) finishes successfully.
    this.syncing$
      .pipe(
        pairwise(),
        filter(([prevSyncing, currentSyncing]) => prevSyncing && !currentSyncing),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.openSnackbar('analysis.synced'));
  }

  private readonly ALERT_DEBOUNCE_TIME = 500;

  // Effects

  // Analyst Management
  private readonly setCurrentAnalyst = this.effect((params$: Observable<{ sampleId: string; caseId: string }>) =>
    params$.pipe(
      tap(({ sampleId }) => {
        this.analysisStateStore.ensureCurrentAnalystState({ sampleId });
      }),
      switchMap(() => EMPTY)
    )
  );

  private loadAnalystsForSample(sampleId: string, caseId: string): void {
    this.analysisStateStore.fetchAnalysisStates({ sampleId, caseId });
    // Forward the URL's historyId (if any) so deep-links and reloads land on
    // the right analyst. On a within-app sample navigation `navigate()` has
    // cleared this param, so the value is null and the store will fall back
    // to its preserve-or-currentUser logic. Read from `route.snapshot` (not
    // an observable) so we capture the value at the moment of the call.
    const preferredHistoryId = this.route.snapshot.queryParamMap.get('historyId') ?? undefined;
    // A breadcrumb cascade names the analyst explicitly for exactly this sample; it outranks
    // both the url and the preserve-current-selection rule — so switching to "me"/another
    // analyst works even when the destination sample also has the analyst we were viewing
    // (e.g. the same AI). Consumed only when the sample matches, then cleared.
    const pending = this.pendingAnalystSelection;
    const preferredAnalystId = pending?.sampleId === sampleId ? pending.analystId : undefined;
    if (preferredAnalystId) this.pendingAnalystSelection = null;
    this.analystStore.resolveAnalystForSample(of({ sampleId, preferredHistoryId, preferredAnalystId }));
  }

  // Analysis State Management
  public readonly updateSampleState = this.effect((state$: Observable<IState>) => {
    return state$.pipe(
      concatLatestFrom(() => [this.sampleId$]),
      switchMap(([state, sampleId]) => {
        this.analysisStateStore.updateSampleState(of({ sampleId, state }));
        return EMPTY;
      })
    );
  });

  readonly updateCaseState = this.effect((state$: Observable<IState>) => {
    return state$.pipe(
      concatLatestFrom(() => this.currentCase$),
      switchMap(([state, currentCase]) => {
        this.analysisStateStore.updateCaseState(
          of({
            state,
            currentCaseId: currentCase.id,
          })
        );

        return of(null);
      })
    );
  });

  readonly setReference = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      concatLatestFrom(() => [this.currentSample$]),
      switchMap(([, sample]) => {
        this.analysisStateStore.setReference(of(sample.id));
        return EMPTY;
      })
    );
  });

  // Asset Management
  private readonly selectAsset = this.effect((assetId$: Observable<string | null>) =>
    assetId$.pipe(
      map((assetId) => {
        this.assetStore.selectAsset(assetId);
        this.microLayout.addViewedAsset(assetId);
      })
    )
  );

  readonly toggleBookmark = this.effect((assetId$: Observable<string>) => {
    return assetId$.pipe(
      concatLatestFrom(() => [this.currentAnalystBookmarkedAsset$, this.currentSample$]),
      switchMap(([assetId, currentBookmarkedId, sample]) => {
        const newBookmarkedId = currentBookmarkedId === assetId ? null : assetId;

        // The store persists the bookmark and optimistically updates its map,
        // which drives `bookmarkedAsset$` — no separate local state needed.
        this.analysisStateStore.toggleBookmark(
          of({
            assetId: newBookmarkedId,
            sampleId: sample.id,
          })
        );

        return EMPTY;
      })
    );
  });

  readonly setAsFavourite = this.effect((assetId$: Observable<string>) => {
    return assetId$.pipe(
      concatLatestFrom(() => [this.currentSample$]),
      switchMap(([assetId, sample]) => {
        this.analysisStateStore.setAsFavourite(of({ assetId, sampleId: sample.id }));
        return EMPTY;
      })
    );
  });

  private readonly fetchReferenceStrip = this.effect((params$: Observable<{ sampleId: string; analyst: Analyst }>) =>
    params$.pipe(
      concatLatestFrom(() => [this.assetStore.assetIds$, this.findingStore.findings$]),
      switchMap(([{ sampleId, analyst }, assetIds, findings]) => {
        if (!assetIds.length) return EMPTY;

        this.microLayout.fetchReferenceStripInfo({
          sampleId,
          analyst,
          assetIds,
          localFindings: Object.values(findings) as FindingParameters[],
        });

        return EMPTY;
      })
    )
  );

  // Progress Management
  readonly loadProgress = this.effect((sampleId$: Observable<string>) => {
    return sampleId$.pipe(
      map((sampleId) => {
        const storageData = JSON.parse(localStorage.getItem(`sample_${sampleId}`));
        const scannedAssets = storageData?.scannedAssets || [];

        this.microLayout.setViewedAssets(scannedAssets);
      })
    );
  });

  readonly saveProgress = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      concatLatestFrom(() => [this.currentSample$, this.viewedAssets$]),
      map(([, currentSample, viewedAssets]) => {
        localStorage.setItem(`sample_${currentSample.id}`, JSON.stringify({ scannedAssets: viewedAssets }));
      })
    );
  });

  // Findings — delegated to MicroFindingEffects
  public fetchAssetFindings(params: { assetId: string; historyId: string; sampleId: string }): void {
    this.findingEffects.fetchAssetFindings(params);
  }

  public fetchSampleFindings(params: { analyst: Analyst; sampleId: string }): void {
    this.findingEffects.fetchSampleFindings(params);
  }

  public updateFinding(params: { id: string; taskType: 'text' | 'classification'; value: string | string[] }): void {
    this.findingEffects.updateFinding(params);
  }

  public createFinding(params: { taskType: any; value: string | string[]; pipelineId: string; taskId: string }): void {
    this.findingEffects.createFinding(params);
  }

  // Counter

  public onCounterLabel(labelId: string): void {
    this.roiStatsEffects.toggleCounterExcluded(labelId);
  }

  // ROI
  // ROI Drawing — delegated to MicroRoiDrawingEffects
  public addRois(params: { overlay: any }): void {
    this.sampleId$.pipe(take(1)).subscribe((sampleId) => {
      this.roiDrawingEffects.addRois({ overlay: params.overlay, sampleId });
    });
  }

  public updateRoiPosition(params: { overlay: OverlayData }): void {
    this.roiDrawingEffects.updateRoiPosition(params);
  }

  public onSelectLabel(labelId: string): void {
    this.labelSelectionEffects.selectLabel(labelId);
  }

  private readonly afterSyncEffect = this.effect((syncing$: Observable<boolean>) =>
    syncing$.pipe(
      pairwise(),
      filter(([prevSyncing, currentSyncing]) => prevSyncing && !currentSyncing),
      concatLatestFrom(() => [this.selectedAnalyst$]),
      filter(([, selectedAnalyst]) => {
        const currentUserId = Parse.User.current()?.id;
        return selectedAnalyst?.id !== currentUserId;
      }),
      tap(() => {
        this.navigateToAnalyst(null);
        this.microLayout.setTabContext(ETabContext.ANALYST);
      }),
      switchMap(() => EMPTY)
    )
  );

  private runSaveActionIfAllowed(action: () => void): void {
    combineLatest([this.aiAnalysisStatus$, this.selectedAnalyst$])
      .pipe(take(1))
      .subscribe(([status, selectedAnalyst]) => {
        if (algorithmAiAnalysisLocked(status, selectedAnalyst)) {
          this.saveBlockedByAiAnalysisSubject.next();
          return;
        }
        action();
      });
  }

  private doSyncChanges(): void {
    combineLatest([this.sampleId$, this.selectedAnalyst$, this.selectedAsset$])
      .pipe(take(1))
      .subscribe(([sampleId, selectedAnalyst, selectedAsset]) => {
        if (!sampleId || !selectedAnalyst?.id) return;

        const currentAnalystId = Parse.User.current()?.id;
        const sourceAnalystId = selectedAnalyst.id;
        if (!currentAnalystId) return;

        sourceAnalystId === currentAnalystId
          ? this.microSyncEffects.syncChanges(
              of({
                sampleId,
                currentAnalystId,
                selectedAssetId: selectedAsset?.id,
              })
            )
          : this.microReviewEffects.reviewAnalysis(
              of({
                sampleId,
                currentAnalystId,
                sourceAnalystId,
              })
            );
      });
  }

  // Context Menu — delegated to MicroContextMenuEffects
  public readonly contextMenuVisible$ = this.contextMenuEffects.contextMenuVisible$;
  public readonly contextMenuPosition$ = this.contextMenuEffects.contextMenuPosition$;
  public readonly selectedItemLabels$ = this.contextMenuEffects.selectedItemLabels$;
  public readonly doubleCheckedOptions$ = this.contextMenuEffects.doubleCheckedOptions$;
  public readonly labelsInAllSelectedRois$ = this.contextMenuEffects.labelsInAllSelectedRois$;
  public readonly labelsInSomeSelectedRois$ = this.contextMenuEffects.labelsInSomeSelectedRois$;

  public showContextMenu(event: MouseEvent): void {
    this.contextMenuEffects.showContextMenu(event);
  }

  public closeContextMenu(): void {
    this.contextMenuEffects.closeContextMenu();
  }

  public onContextMenuOptionSelected(labelId: string): void {
    this.contextMenuEffects.onContextMenuOptionSelected(labelId);
  }

  public onContextMenuOptionDeselected(labelId: string): void {
    this.contextMenuEffects.onContextMenuOptionDeselected(labelId);
  }
}
